import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CitesDirection, CitesPermit, CitesPermitStatus, CitesSpeciesQuota, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip } from '../../ui/components';

const AUTHORITY_ROLES = ['CITES_AUTHORITY'];
const STATUSES: CitesPermitStatus[] = ['APPLIED', 'UNDER_REVIEW', 'APPROVED', 'REJECTED', 'ISSUED', 'USED', 'EXPIRED', 'REVOKED'];

type Tab = 'quotas' | 'permits';

/** CITES species trade permits + the annual quota ledger the CITES_AUTHORITY negotiates (M31). */
export function Cites() {
  const { persona } = useAuth();
  const isAuthority = persona?.roles.includes('CITES_AUTHORITY') ?? false;
  const [tab, setTab] = useState<Tab>('permits');

  return (
    <div>
      <PageHeader title="CITES Permits" subtitle="Species trade permits (import/export) against a legally negotiated annual quota ledger (M31)." />
      <TabStrip
        tabs={[
          { key: 'permits', label: 'Permits' },
          { key: 'quotas', label: 'Species quotas' },
        ]}
        active={tab}
        onChange={setTab}
      />
      {!isAuthority ? <RoleNotice roles={AUTHORITY_ROLES} /> : null}
      {tab === 'quotas' ? <QuotasTab isAuthority={isAuthority} /> : <PermitsTab isAuthority={isAuthority} />}
    </div>
  );
}

function QuotasTab({ isAuthority }: { isAuthority: boolean }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [showSet, setShowSet] = useState(false);
  const quotasQ = useApiQuery<{ items: CitesSpeciesQuota[] }>(isAuthority ? `/api/v1/m31/cites-quotas?year=${year}` : null);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <input className="field-input !w-auto" type="number" value={year} onChange={(e) => setYear(Number.parseInt(e.target.value, 10) || year)} />
        {isAuthority ? (
          <button className="btn-primary" onClick={() => setShowSet(true)}>
            + Set / update quota
          </button>
        ) : null}
      </div>
      {isAuthority ? (
        <>
          {quotasQ.error ? <ErrorNote message={quotasQ.error} /> : null}
          <div className="card overflow-x-auto">
            {quotasQ.loading ? (
              <Spinner />
            ) : (quotasQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No quotas set for this year" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Species</th>
                    <th className="table-head">Direction</th>
                    <th className="table-head">Annual quota</th>
                    <th className="table-head">Consumed</th>
                    <th className="table-head">Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {quotasQ.data!.items.map((q) => (
                    <tr key={q.id}>
                      <td className="table-cell">
                        {q.commonName} <span className="font-mono text-xs text-slate-400">({q.speciesCode})</span>
                      </td>
                      <td className="table-cell">
                        <StatusBadge status={q.direction} />
                      </td>
                      <td className="table-cell tabular-nums">{q.annualQuota}</td>
                      <td className="table-cell tabular-nums">{q.consumedQuota}</td>
                      <td className={`table-cell tabular-nums font-semibold ${q.annualQuota - q.consumedQuota <= 0 ? 'text-rose-600' : 'text-seagreen-600'}`}>
                        {q.annualQuota - q.consumedQuota}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}
      {showSet ? (
        <SetQuotaModal
          onClose={() => setShowSet(false)}
          onDone={() => {
            setShowSet(false);
            quotasQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function SetQuotaModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [speciesCode, setSpeciesCode] = useState('');
  const [commonName, setCommonName] = useState('');
  const [direction, setDirection] = useState<CitesDirection>('EXPORT');
  const [year, setYear] = useState(new Date().getFullYear());
  const [annualQuota, setAnnualQuota] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m31/cites-quotas', {
        speciesCode,
        commonName,
        direction,
        year,
        annualQuota: Number.parseInt(annualQuota, 10),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to set quota');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Set / update species quota" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Species code">
            <input className="field-input" value={speciesCode} onChange={(e) => setSpeciesCode(e.target.value)} placeholder="e.g. TRIDACNA_GIGAS" />
          </Field>
          <Field label="Common name">
            <input className="field-input" value={commonName} onChange={(e) => setCommonName(e.target.value)} placeholder="Giant clam" />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Direction">
            <select className="field-input" value={direction} onChange={(e) => setDirection(e.target.value as CitesDirection)}>
              <option value="EXPORT">Export</option>
              <option value="IMPORT">Import</option>
            </select>
          </Field>
          <Field label="Year">
            <input className="field-input" type="number" value={year} onChange={(e) => setYear(Number.parseInt(e.target.value, 10) || year)} />
          </Field>
          <Field label="Annual quota (units)">
            <input className="field-input" type="number" min="0" value={annualQuota} onChange={(e) => setAnnualQuota(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || !speciesCode || !commonName || !annualQuota} onClick={submit}>
            {busy ? 'Saving…' : 'Save quota'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function PermitsTab({ isAuthority }: { isAuthority: boolean }) {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showApply, setShowApply] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m31/cites-permits?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<CitesPermit>>(isAuthority ? path : null);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="Ref or species…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-primary">
            Search
          </button>
        </form>
        <button className="btn-secondary" onClick={() => setShowApply(true)}>
          + New CITES permit application
        </button>
      </div>

      {isAuthority ? (
        <>
          {listQ.error ? <ErrorNote message={listQ.error} /> : null}
          <div className="card overflow-x-auto">
            {listQ.loading ? (
              <Spinner />
            ) : (listQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No CITES permits match" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Ref</th>
                    <th className="table-head">Species</th>
                    <th className="table-head">Direction</th>
                    <th className="table-head">Qty</th>
                    <th className="table-head">Fee</th>
                    <th className="table-head">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.items.map((p) => (
                    <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(p.id)}>
                      <td className="table-cell font-mono text-xs font-medium">{p.refNo}</td>
                      <td className="table-cell">{p.commonName}</td>
                      <td className="table-cell">
                        <StatusBadge status={p.direction} />
                      </td>
                      <td className="table-cell tabular-nums">{p.quantity}</td>
                      <td className="table-cell text-xs">
                        {scr(p.feeAmountScr)} · <StatusBadge status={p.feeStatus} />
                      </td>
                      <td className="table-cell">
                        <StatusBadge status={p.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}

      {selectedId ? <PermitDetailModal id={selectedId} onClose={() => setSelectedId(null)} onChanged={() => listQ.reload()} /> : null}
      {showApply ? (
        <ApplyModal
          onClose={() => setShowApply(false)}
          onDone={() => {
            setShowApply(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function PermitDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<CitesPermit>(`/api/v1/m31/cites-permits/${id}`);
  const p = q.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decideNotes, setDecideNotes] = useState('');
  const [approve, setApprove] = useState<boolean | null>(null);
  const [revokeReason, setRevokeReason] = useState('');

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={p ? p.refNo : 'CITES permit'} onClose={onClose}>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {p ? (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <Detail label="Species" value={`${p.commonName} (${p.speciesCode})`} />
            <Detail label="Direction / qty" value={<span><StatusBadge status={p.direction} /> · {p.quantity}</span>} />
            <Detail label="Status" value={<StatusBadge status={p.status} />} />
            <Detail label="Fee" value={`${scr(p.feeAmountScr)} (${p.feeStatus})`} />
            <Detail label="Country partner" value={p.countryPartner} />
            {p.permitSerial ? <Detail label="Permit serial" value={p.permitSerial} mono /> : null}
            {p.validTo ? <Detail label="Valid to" value={formatDate(p.validTo)} /> : null}
          </div>
          <p className="text-xs text-slate-500">Purpose: {p.purpose}</p>
          {p.decisionNotes ? <p className="text-xs text-slate-500">Decision notes: {p.decisionNotes}</p> : null}
          {p.revokedReason ? <ErrorNote message={`Revoked: ${p.revokedReason}`} /> : null}
          {p.feeStatus !== 'PAID' ? <p className="text-xs text-amber-600">Fee confirmation arrives via the SeyPay webhook (service-token only).</p> : null}

          {p.status === 'APPLIED' ? (
            <button className="btn-primary" disabled={busy || p.feeStatus !== 'PAID'} onClick={() => act(() => api.post(`/api/v1/m31/cites-permits/${id}/start-review`))}>
              Start review
            </button>
          ) : null}

          {p.status === 'UNDER_REVIEW' ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <div className="text-sm font-semibold text-slate-700">Decision</div>
              <p className="text-xs text-slate-500">Approving reserves quota units from the species' current-year ledger — fails with 409 if none remain.</p>
              <div className="flex gap-2">
                <button className={`btn-success ${approve === true ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setApprove(true)}>
                  Approve
                </button>
                <button className={`btn-danger ${approve === false ? 'ring-2 ring-offset-1' : ''}`} onClick={() => setApprove(false)}>
                  Reject
                </button>
              </div>
              {approve !== null ? (
                <>
                  <input className="field-input" placeholder="Notes (optional)" value={decideNotes} onChange={(e) => setDecideNotes(e.target.value)} />
                  <button className={approve ? 'btn-success' : 'btn-danger'} disabled={busy} onClick={() => act(() => api.post(`/api/v1/m31/cites-permits/${id}/decide`, { approve, notes: decideNotes || undefined }))}>
                    Confirm {approve ? 'approval' : 'rejection'}
                  </button>
                </>
              ) : null}
            </div>
          ) : null}

          {p.status === 'APPROVED' ? (
            <button className="btn-primary" disabled={busy} onClick={() => act(() => api.post(`/api/v1/m31/cites-permits/${id}/issue`))}>
              Issue permit (DocVault)
            </button>
          ) : null}

          {p.status === 'ISSUED' ? (
            <div className="flex gap-2">
              <input className="field-input" placeholder="Revoke reason" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} />
              <button className="btn-danger" disabled={busy || revokeReason.trim().length < 3} onClick={() => act(() => api.post(`/api/v1/m31/cites-permits/${id}/revoke`, { reason: revokeReason }))}>
                Revoke
              </button>
            </div>
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">
            Applied {formatDateTime(p.createdAt)} · last updated {formatDateTime(p.updatedAt)}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function ApplyModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [speciesCode, setSpeciesCode] = useState('');
  const [commonName, setCommonName] = useState('');
  const [direction, setDirection] = useState<CitesDirection>('EXPORT');
  const [quantity, setQuantity] = useState('1');
  const [purpose, setPurpose] = useState('');
  const [countryPartner, setCountryPartner] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m31/cites-permits', {
        speciesCode,
        commonName,
        direction,
        quantity: Number.parseInt(quantity, 10),
        purpose,
        countryPartner,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to submit application');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New CITES permit application" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Species code">
            <input className="field-input" value={speciesCode} onChange={(e) => setSpeciesCode(e.target.value)} />
          </Field>
          <Field label="Common name">
            <input className="field-input" value={commonName} onChange={(e) => setCommonName(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Direction">
            <select className="field-input" value={direction} onChange={(e) => setDirection(e.target.value as CitesDirection)}>
              <option value="EXPORT">Export</option>
              <option value="IMPORT">Import</option>
            </select>
          </Field>
          <Field label="Quantity">
            <input className="field-input" type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </Field>
        </div>
        <Field label="Country partner">
          <input className="field-input" value={countryPartner} onChange={(e) => setCountryPartner(e.target.value)} placeholder="e.g. France" />
        </Field>
        <Field label="Purpose">
          <textarea className="field-input" rows={2} value={purpose} onChange={(e) => setPurpose(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" disabled={busy || !speciesCode || !commonName || !countryPartner || purpose.trim().length < 3} onClick={submit}>
            {busy ? 'Submitting…' : 'Submit application'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
